import { Redirect } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';
import { BookingDetailsCard } from '~/components/BookingDetailsCard';
import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { bookingReference } from '~/lib/bookingDisplay';
import { colors, fontFamily, spacing } from '~/ui/tokens';

// Development-only visual QA. These records are deliberately labelled as samples
// and never fetched from, or written to, a customer account.
const base: Wire.OrderSummary = {
    orderId: 100071767,
    partnerOrderId: null,
    status: 'confirmed',
    paymentStatus: 'paid',
    paymentAmount: { amount: '24665', currencyCode: 'INR' },
    refundStatus: null,
    refundAmount: null,
    cancelledAt: null,
    isCancellable: false,
    freeCancellationBefore: '2026-10-03T12:00:00.000Z',
    hotelId: '',
    hotelName: 'Sample Paris Hotel',
    hotelAddress: '16 Rue Vernet, Paris 75008',
    hotelStarRating: 4,
    hotelReviewScore: 4.2,
    hotelReviewCount: 318,
    thumbnailUrl: null,
    checkIn: '2026-10-06',
    checkOut: '2026-10-10',
    roomName: 'Junior Suite (king bed)',
    roomCount: 1,
    guestCount: 2,
    total: { amount: '24665', currencyCode: 'INR' }
};

const samples: Array<{ title: string; note: string; order: Wire.OrderSummary }> = [
    { title: '1. Payment received · booking confirmed', note: 'Sample order ID and stored cancellation deadline.', order: base },
    { title: '1b. Hotel order exists · payment status absent', note: 'Legacy order has an ID, but no captured payment record. Payment is not claimed as completed.', order: { ...base, paymentStatus: undefined, paymentAmount: null } },
    { title: '2. Payment received · confirmation pending', note: 'No booking ID until the supplier confirms.', order: { ...base, orderId: 0, status: 'processing', refundStatus: null } },
    { title: '3. Payment received · booking failed · refund initiated', note: 'The refund request was accepted; bank processing typically takes 7–10 working days.', order: { ...base, orderId: 0, status: 'failed', paymentStatus: 'refund_pending', refundStatus: 'initiated', refundAmount: base.paymentAmount } },
    { title: '3b. Refund request needs manual review', note: 'A request that was not accepted never claims initiation or a bank timeline.', order: { ...base, orderId: 0, status: 'failed', paymentStatus: 'refund_pending', refundStatus: 'pending_manual', refundAmount: null } },
    { title: '4. Same booking · refund completed', note: 'This replaces state 3 only when refund completion is recorded.', order: { ...base, orderId: 0, status: 'failed', paymentStatus: 'refunded', refundStatus: 'completed', refundAmount: base.paymentAmount } },
    { title: '5. Payment failed · booking never created', note: 'No booking ID or refund claim.', order: { ...base, orderId: 0, status: 'payment_failed', paymentStatus: 'failed', paymentAmount: null, refundStatus: null } },
    { title: '5b. Booking stopped · payment outcome unverified', note: 'Legacy records with pending payment status cannot claim a failed charge.', order: { ...base, orderId: 0, status: 'payment_failed', paymentStatus: 'pending', paymentAmount: null, refundStatus: null } },
    { title: '6. Confirmed booking · later cancelled', note: 'Cancellation date exists; refund amount is unknown.', order: { ...base, status: 'cancelled', paymentStatus: 'paid', cancelledAt: '2026-09-29T10:00:00.000Z', refundStatus: null, refundAmount: null } },
    { title: '7. Sparse supplier data', note: 'Missing optional hotel metadata is omitted instead of filled with sample copy.', order: { ...base, hotelAddress: '', hotelStarRating: 0, hotelReviewScore: 0, hotelReviewCount: 0, roomName: 'Premier Room', thumbnailUrl: null } }
];

export default function QaBookingCards() {
    if (!__DEV__) return <Redirect href="/" />;
    return <Screen><TopNav /><View style={styles.page}>
        <Text style={styles.title}>Booking card QA</Text>
        <Text style={styles.warning}>DEVELOPMENT SAMPLE DATA · These are visual state fixtures, not customer bookings. Open Your trips to inspect real backend records.</Text>
        {samples.map(({ title, note, order }) => <View key={title} style={styles.section}>
            <Text style={styles.sectionTitle}>{title}</Text>
            <Text style={styles.note}>{note}</Text>
            <BookingDetailsCard order={order} bookingRef={bookingReference(order.orderId, order.status)} />
        </View>)}
    </View></Screen>;
}

const styles = StyleSheet.create({
    page: { width: '100%', maxWidth: 780, alignSelf: 'center', paddingHorizontal: spacing(2), paddingVertical: spacing(4), gap: spacing(4) },
    title: { fontFamily: fontFamily.bold, fontSize: 26, color: colors.ink },
    warning: { fontFamily: fontFamily.semibold, fontSize: 13, lineHeight: 20, color: colors.brand },
    section: { gap: spacing(1) },
    sectionTitle: { fontFamily: fontFamily.bold, fontSize: 18, color: colors.ink },
    note: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft }
});
